import type { JSX } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { Portrait } from "../components/ui/editorial/Portrait.js";
import { useCurator } from "../useCurator.js";

interface ProfileSection {
  kicker: string;
  body: string;
}

interface ProfileData {
  id: string;
  num: string;
  headlineFirst: string;
  headlineVerb: string;
  headlineTail: string;
  /** Имя в дательном падеже — для «Ответить Ане». */
  nameDative: string;
  age: number;
  city: string;
  role: string;
  pull: string;
  /** Развёрнутый портрет по ответам из анкеты Спутника. */
  sections: ProfileSection[];
  portraitSrc?: string;
}

const DEFAULT_PROFILE: ProfileData = {
  id: "04",
  num: "№ 04",
  headlineFirst: "Аня,",
  headlineVerb: "которая читает",
  headlineTail: "по вечерам.",
  nameDative: "Ане",
  age: 28,
  city: "Москва",
  role: "Редактор",
  pull: "Тишина — это не отсутствие звука. Это разрешение не спешить.",
  sections: [
    {
      kicker: "вечера",
      body: "Чаще всего — дома, с книгой и чаем. Иногда медленная прогулка вдоль реки, но обязательно без маршрута. Говорит: «движение ради движения — утомительно».",
    },
    {
      kicker: "ценит в людях",
      body: "Внимательность и чувство паузы. Людей, которые не торопятся отвечать. Не любит громкость — ни в голосе, ни в одежде.",
    },
    {
      kicker: "разговор до утра",
      body: "Про книги, про перевод, про редактуру. Может час обсуждать ритм одного абзаца. Считает, что хорошие разговоры похожи на музыку — с тишиной внутри.",
    },
    {
      kicker: "недавно радовало",
      body: "Перечитала «Казус Кукоцкого» Улицкой — второй раз, иначе. Нашла фразу, которую теперь носит в голове неделю.",
    },
    {
      kicker: "не выносит",
      body: "Спешку, пустую болтовню, small talk в дороге. Когда не слушают, а ждут своей реплики.",
    },
  ],
};

const PROFILES: Record<string, ProfileData> = {
  "04": DEFAULT_PROFILE,
  "03": {
    id: "03",
    num: "№ 03",
    headlineFirst: "Марина,",
    headlineVerb: "которая снимает",
    headlineTail: "короткое кино.",
    nameDative: "Марине",
    age: 31,
    city: "Москва",
    role: "Режиссёр",
    pull: "Мне нужны вечера, которые не заканчиваются в полночь.",
    sections: [
      {
        kicker: "вечера",
        body: "Одна или с парой близких. Вместо вечеринок — хорошее кафе на краю города, где можно долго сидеть и никому нет дела до часов.",
      },
      {
        kicker: "ценит в людях",
        body: "Юмор без надрыва и умение молчать вовремя. Не переносит наигранность и самопрезентацию.",
      },
      {
        kicker: "разговор до утра",
        body: "Про кино, но больше — про кадр. Про то, как свет из окна в четыре утра меняет лицо. Про Тарковского — осторожно, не сразу.",
      },
      {
        kicker: "недавно радовало",
        body: "Вернулась с небольшого показа. Говорит: «один короткий метр был похож на сон, который я хотела забыть, но не смогла».",
      },
      {
        kicker: "не выносит",
        body: "Спешку, громкость, small talk на фестивалях. Когда пытаются понравиться.",
      },
    ],
  },
  "02": {
    id: "02",
    num: "№ 02",
    headlineFirst: "Лиза,",
    headlineVerb: "которая замечает",
    headlineTail: "старые дома.",
    nameDative: "Лизе",
    age: 29,
    city: "Москва",
    role: "Архитектор",
    pull: "Город — это не адреса. Это дворы, в которые никто не заглядывает.",
    sections: [
      {
        kicker: "вечера",
        body: "Медленные прогулки и тёплые кофейни во дворах. Не любит туристические места — ценит дворы, в которые ходят только местные.",
      },
      {
        kicker: "ценит в людях",
        body: "Внимание к деталям и к месту. Людей, которые замечают не то, что нужно заметить, а то, что никто не замечает.",
      },
      {
        kicker: "разговор до утра",
        body: "Про архитектуру, про свет, про то, как устроены старые лестницы. Про книги о городе. Про Бродского — «он архитектор в языке».",
      },
      {
        kicker: "недавно радовало",
        body: "Нашла двор в районе Покровки, где всё сохранилось с двадцатых. Сфотографировала фасад и теперь носит его в голове как открытку.",
      },
      {
        kicker: "не выносит",
        body: "Снобизм и наигранность. Когда ставят вид выше смысла.",
      },
    ],
  },
  "01": {
    id: "01",
    num: "№ 01",
    headlineFirst: "Катя,",
    headlineVerb: "которая пишет",
    headlineTail: "на полях.",
    nameDative: "Кате",
    age: 26,
    city: "Москва",
    role: "Поэт",
    pull: "Иногда одна строка — это всё, что нужно сказать в этот день.",
    sections: [
      {
        kicker: "вечера",
        body: "Сумерки — единственное её время. Пишет, читает, иногда выходит к реке — одна или с тетрадью. Компания тоже бывает, но редко и с разбором.",
      },
      {
        kicker: "ценит в людях",
        body: "Честность и внимание к интонации. Людей, которые умеют молчать. Не переносит тех, кто «говорит много и не говорит ничего».",
      },
      {
        kicker: "разговор до утра",
        body: "Про поэзию, про переводы, про письма — бумажные, длинные, на несколько страниц. Может час спорить о том, когда поставить тире.",
      },
      {
        kicker: "недавно радовало",
        body: "Дописала цикл из семи стихов про свет в марте. Один из них, кажется, «настоящий» — а это случается нечасто.",
      },
      {
        kicker: "не выносит",
        body: "Пустую болтовню и срочные сообщения. Просит писать письмами — даже если короткими.",
      },
    ],
  },
};

export function Profile(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";

  // PROFILES — демо для /brainstorm (id 01–04). В проде для неизвестного
  // ID показываем честный empty state вместо фейкового портрета Ани.
  const p = PROFILES[id];

  if (!p) {
    return (
      <Screen tone="paper">
        <div className="px-[22px] pt-[48px]">
          <Topline left="COMPANION" right="портрет" />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-[22px] text-center">
          <h1 className="font-display font-light text-[32px] leading-[1.05] text-ink">
            <em className="italic text-velvet">Портрет</em>
            <br />
            ещё готовится.
          </h1>
          <p className="mt-[16px] max-w-[320px] font-display italic font-light text-[15px] leading-[1.5] text-ink/65">
            {curator.firstName} допишет развёрнутый портрет к следующему письму.
          </p>
        </div>
        <div className="px-[22px] pb-[24px]">
          <button
            type="button"
            onClick={() => {
              navigate("/home");
            }}
            className="editorial-meta text-ink/55 hover:text-ink transition-colors"
          >
            ← в редакцию
          </button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="портрет" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[30px]">
        <div className="editorial-kicker text-accent">Портрет · {p.num}</div>

        <h1 className="mt-[16px] font-display font-light leading-[0.98] tracking-[-0.028em] text-ink text-[42px]">
          {p.headlineFirst}
          <br />
          <em className="italic text-velvet">{p.headlineVerb}</em>
          <br />
          {p.headlineTail}
        </h1>

        <div className="mt-[16px] flex items-center gap-[10px] editorial-meta text-ink/60">
          <span>
            {p.age} · {p.city}
          </span>
          <span className="h-[2px] w-[2px] rounded-full bg-ink/40" />
          <span>{p.role}</span>
        </div>

        <div className="mt-[24px]">
          <Portrait {...(p.portraitSrc ? { src: p.portraitSrc } : {})} alt={p.headlineFirst} />
        </div>

        <figure className="mt-[24px] pl-[16px] border-l-2 border-accent">
          <blockquote className="font-display italic font-light text-[17px] leading-[1.4] text-ink/85">
            <span className="text-accent font-normal">«</span>
            {p.pull}
            <span className="text-accent font-normal">»</span>
          </blockquote>
        </figure>

        {/* Развёрнутый портрет — из ответов её анкеты */}
        <div className="mt-[30px]">
          <div className="flex items-center gap-[12px] editorial-meta text-ink/55">
            <span className="h-px flex-1 bg-ink/14" />
            <span>её портрет · из анкеты</span>
            <span className="h-px flex-1 bg-ink/14" />
          </div>

          <div className="mt-[16px] space-y-[18px]">
            {p.sections.map((s) => (
              <div key={s.kicker} className="border-t border-ink/12 pt-[14px]">
                <div className="editorial-kicker text-accent">{s.kicker}</div>
                <p className="mt-[8px] font-display font-light text-[16px] leading-[1.55] text-ink/85">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-[22px] pb-[24px] pt-[24px]">
        <button
          type="button"
          onClick={() => {
            navigate(`/letter/${p.id}`);
          }}
          className="mb-[14px] editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← к письму
        </button>
        <PrimaryButton
          onClick={() => {
            navigate(`/feedback/${p.id}`);
          }}
        >
          Ответить {curator.footerLabel}
        </PrimaryButton>
      </div>
    </Screen>
  );
}
